import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Music, Mic } from 'lucide-react';
import type { AudioTrack } from './types';

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface BrandedVideoPlayerProps {
  videoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  aspectMode: 'portrait' | 'landscape';
  audioTracks?: AudioTrack[];
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export function BrandedVideoPlayer({
  videoUrl,
  primaryColor,
  secondaryColor,
  aspectMode,
  audioTracks,
  onPlay,
  onPause,
  onEnded,
}: BrandedVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [buffered, setBuffered] = useState(0);
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const [trackVolumes, setTrackVolumes] = useState<Record<string, number>>({});
  const [trackMuted, setTrackMuted] = useState<Record<string, boolean>>({});
  const controlsTimeout = useRef<ReturnType<typeof setTimeout>>();

  const activeTracks = (audioTracks ?? []).filter((t) => t.is_active);
  const hasAudioTracks = activeTracks.length > 0;

  useEffect(() => {
    const vols: Record<string, number> = {};
    activeTracks.forEach((t) => { vols[t.id] = t.volume; });
    setTrackVolumes(vols);
  }, [audioTracks]);

  useEffect(() => {
    const currentIds = new Set(activeTracks.map((t) => t.id));
    audioRefs.current.forEach((audio, id) => {
      if (!currentIds.has(id)) {
        audio.pause();
        audio.src = '';
        audioRefs.current.delete(id);
      }
    });

    activeTracks.forEach((track) => {
      if (!audioRefs.current.has(track.id)) {
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = track.audio_url;
        audio.volume = track.volume;
        audioRefs.current.set(track.id, audio);
      }
    });
  }, [audioTracks]);

  const syncAudioToVideo = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    activeTracks.forEach((track) => {
      const audio = audioRefs.current.get(track.id);
      if (!audio) return;

      const vol = trackVolumes[track.id] ?? track.volume;
      const muted = trackMuted[track.id] ?? false;
      audio.volume = muted ? 0 : vol;

      const targetTime = v.currentTime - (track.start_offset_seconds ?? 0);
      if (targetTime < 0 || (audio.duration && targetTime > audio.duration)) {
        audio.pause();
        return;
      }

      if (Math.abs(audio.currentTime - targetTime) > 0.3) {
        audio.currentTime = targetTime;
      }

      if (!v.paused && audio.paused) {
        audio.play().catch(() => {});
      } else if (v.paused && !audio.paused) {
        audio.pause();
      }
    });
  }, [activeTracks, trackVolumes, trackMuted]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const container = playerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      await container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
    if (v.buffered.length > 0) {
      setBuffered((v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
    }
    syncAudioToVideo();
  }, [syncAudioToVideo]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    const bar = progressRef.current;
    if (!v || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
    syncAudioToVideo();
  }, [syncAudioToVideo]);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    syncAudioToVideo();
    onPlay?.();
  }, [syncAudioToVideo, onPlay]);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
    audioRefs.current.forEach((audio) => audio.pause());
    onPause?.();
  }, [onPause]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
    audioRefs.current.forEach((audio) => { audio.pause(); audio.currentTime = 0; });
    onEnded?.();
  }, [onEnded]);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  useEffect(() => {
    return () => {
      audioRefs.current.forEach((audio) => { audio.pause(); audio.src = ''; });
      audioRefs.current.clear();
    };
  }, []);

  const handleTrackVolume = useCallback((trackId: string, vol: number) => {
    setTrackVolumes((prev) => ({ ...prev, [trackId]: vol }));
    const audio = audioRefs.current.get(trackId);
    if (audio) audio.volume = vol;
  }, []);

  const handleTrackMuteToggle = useCallback((trackId: string) => {
    setTrackMuted((prev) => {
      const next = { ...prev, [trackId]: !prev[trackId] };
      const audio = audioRefs.current.get(trackId);
      if (audio) audio.volume = next[trackId] ? 0 : (trackVolumes[trackId] ?? 0.8);
      return next;
    });
  }, [trackVolumes]);

  const gradientStyle = { background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` };

  const containerProps = aspectMode === 'portrait'
    ? { style: { aspectRatio: '9/16', maxWidth: 'min(420px, 100%)' } as React.CSSProperties, className: 'relative group rounded-xl overflow-hidden bg-black shadow-2xl shadow-black/60 border border-slate-800/50 max-h-[85vh]' }
    : { className: 'relative group rounded-xl overflow-hidden bg-black shadow-2xl shadow-black/60 border border-slate-800/50' };

  const videoClassName = aspectMode === 'portrait'
    ? 'w-full h-full object-contain cursor-pointer'
    : 'w-full cursor-pointer';

  return (
    <div
      ref={playerRef}
      {...containerProps}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { if (isPlaying) setShowControls(false); }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        className={videoClassName}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onEnded={handleVideoEnded}
      />

      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer" onClick={togglePlay}>
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 hover:bg-white/25 transition-colors">
            <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Audio tracks indicator */}
      {hasAudioTracks && (
        <div className={`absolute top-3 right-3 flex items-center gap-1.5 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
          {activeTracks.some((t) => t.track_type === 'music') && (
            <div className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <Music className="w-3.5 h-3.5 text-white/70" />
            </div>
          )}
          {activeTracks.some((t) => t.track_type === 'voiceover') && (
            <div className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <Mic className="w-3.5 h-3.5 text-white/70" />
            </div>
          )}
        </div>
      )}

      {/* Audio tracks panel */}
      {showAudioPanel && hasAudioTracks && (
        <div className="absolute top-0 right-0 bottom-16 w-56 bg-black/80 backdrop-blur-md border-l border-slate-700/50 p-3 space-y-3 overflow-y-auto z-10" onClick={(e) => e.stopPropagation()}>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Audio Tracks</p>
          {activeTracks.map((track) => {
            const muted = trackMuted[track.id] ?? false;
            const vol = trackVolumes[track.id] ?? track.volume;
            return (
              <div key={track.id} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  {track.track_type === 'music' ? (
                    <Music className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  ) : (
                    <Mic className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  )}
                  <span className="text-xs text-white truncate flex-1">
                    {track.label || (track.track_type === 'music' ? 'Music' : 'Voiceover')}
                  </span>
                  <button
                    onClick={() => handleTrackMuteToggle(track.id)}
                    className="text-slate-400 hover:text-white transition-colors shrink-0"
                  >
                    {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={vol}
                  onChange={(e) => handleTrackVolume(track.id, parseFloat(e.target.value))}
                  className="w-full h-1 bg-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
              </div>
            );
          })}
        </div>
      )}

      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-12 pb-3 px-3 sm:px-4 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div
          ref={progressRef}
          className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 group/progress relative"
          onClick={handleProgressClick}
        >
          <div className="absolute top-0 left-0 h-full bg-white/10 rounded-full" style={{ width: `${buffered}%` }} />
          <div className="absolute top-0 left-0 h-full rounded-full transition-[width] duration-100" style={{ width: `${progress}%`, ...gradientStyle }} />
          <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity" style={{ left: `calc(${progress}% - 7px)` }} />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="w-8 h-8 flex items-center justify-center text-white hover:opacity-80 transition-colors">
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button onClick={toggleMute} className="w-8 h-8 flex items-center justify-center text-white hover:opacity-80 transition-colors">
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            {hasAudioTracks && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAudioPanel(!showAudioPanel); }}
                className={`w-8 h-8 flex items-center justify-center transition-colors ${showAudioPanel ? 'text-purple-400' : 'text-white hover:opacity-80'}`}
                title="Audio tracks"
              >
                <Music className="w-4 h-4" />
              </button>
            )}
            <span className="text-xs text-white/70 font-mono tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <button onClick={toggleFullscreen} className="w-8 h-8 flex items-center justify-center text-white hover:opacity-80 transition-colors">
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
